import React, { useLayoutEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { UserSearchInput } from './components/UserSearchInput';
import { TO_UFS_INPUT_CLASS, TO_UFS_INPUT_CLASS_DISABLED } from './constants';

function cellUserId(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'object') return String(v._id ?? v.id ?? '').trim();
  return String(v).trim();
}

/**
 * Combined To + UFS: stores user IDs for backend; displays names in the UI.
 * Fields are editable only when disabled=false (typically when workflow status is DRAFT or RETURNED).
 * @param {Object} props
 * @param {{ to: string, ufsList: string[], toDisplayName?: string, ufsDisplayNames?: string[] }} props.value - to/ufsList are IDs; *DisplayName are for display only
 * @param {function({ to: string, ufsList: string[], toDisplayName?: string, ufsDisplayNames?: string[] }): void} props.onChange
 */
export const ToAndUfs = ({
  labelTo = 'To',
  labelUfs = 'UFS',
  value,
  onChange,
  required = false,
  placeholderTo = 'Recipient',
  placeholderUfs = 'Add UFS entry',
  disabled = false,
  lockTo = false,
  lockedUfsIndexes = [],
  errorTo,
  errorUfs,
  labelWidthRem = 6,
  labelPosition = 'left',
}) => {
  const { to = '', ufsList = [''], toDisplayName = '', ufsDisplayNames = [] } = value || {};
  const items = Array.isArray(ufsList) ? ufsList : ufsList ? [ufsList] : [''];
  const displayNames = Array.isArray(ufsDisplayNames) ? ufsDisplayNames : [];
  const isTop = labelPosition === 'top';
  const gridStyle = isTop ? undefined : { gridTemplateColumns: `${labelWidthRem}rem 1fr` };
  const lockedSet = new Set(Array.isArray(lockedUfsIndexes) ? lockedUfsIndexes : []);

  /**
   * First paint (or first time routing becomes non-empty) captures which slots were prefilled so they
   * stay read-only even if lock meta is late; user-added UFS rows after that are not in this snapshot.
   */
  const [assigneeSeed, setAssigneeSeed] = useState(null);
  useLayoutEffect(() => {
    if (assigneeSeed != null || !value) return;
    const t = cellUserId(value.to);
    const u = Array.isArray(value.ufsList) ? value.ufsList.map(cellUserId) : [];
    if (!t && !u.some(Boolean)) return;
    setAssigneeSeed({ to: t, ufs: u });
  }, [value, assigneeSeed]);

  const isToLocked = lockTo === true || Boolean(assigneeSeed?.to);
  const isUfsRowLocked = (idx) =>
    lockedSet.has(idx) || Boolean(assigneeSeed?.ufs && cellUserId(assigneeSeed.ufs[idx]));

  const handleToChange = (id, displayName) => {
    if (disabled || isToLocked) return;
    onChange({ to: id ?? '', ufsList: items, toDisplayName: displayName ?? '', ufsDisplayNames: displayNames });
  };

  const handleUfsItemChange = (idx, id, displayName) => {
    if (disabled || isUfsRowLocked(idx)) return;
    const next = [...items];
    const nextDisplay = [...displayNames];
    next[idx] = id ?? '';
    nextDisplay[idx] = displayName ?? '';
    onChange({ to, ufsList: next, toDisplayName, ufsDisplayNames: nextDisplay });
  };

  const handleUfsAdd = () =>
    onChange({ to, ufsList: [...items, ''], toDisplayName, ufsDisplayNames: [...displayNames, ''] });
  const handleUfsRemove = (idx) => {
    if (isUfsRowLocked(idx)) return;
    if (items.length <= 1) return;
    const next = items.filter((_, i) => i !== idx);
    const nextDisplay = displayNames.filter((_, i) => i !== idx);
    onChange({ to, ufsList: next, toDisplayName, ufsDisplayNames: nextDisplay });
  };

  const labelToEl = (
    <label className="text-sm font-medium text-primary-dark">
      {labelTo} {required && <span className="text-red-500">*</span>}
    </label>
  );
  const labelUfsEl = (
    <label className="text-sm font-medium text-primary-dark">{labelUfs}</label>
  );

  const toInputClass = (disabled || isToLocked) ? TO_UFS_INPUT_CLASS_DISABLED : TO_UFS_INPUT_CLASS;
  const toField = (
    <UserSearchInput
      value={to}
      valueDisplay={toDisplayName}
      onChange={handleToChange}
      placeholder={placeholderTo}
      disabled={disabled || isToLocked}
      error={errorTo}
      inputClassName={toInputClass}
    />
  );

  const ufsInputRow = (item, idx) => {
    const rowLocked = disabled || isUfsRowLocked(idx);
    return (
    <div key={idx} className="min-w-0 flex gap-2 items-center w-full">
      <div className="min-w-0 flex-1">
        <UserSearchInput
          value={item}
          valueDisplay={displayNames[idx]}
          onChange={(id, name) => handleUfsItemChange(idx, id, name)}
          placeholder={placeholderUfs}
          disabled={rowLocked}
          inputClassName={rowLocked ? TO_UFS_INPUT_CLASS_DISABLED : TO_UFS_INPUT_CLASS}
        />
      </div>
      {idx === items.length - 1 ? (
        <button
          type="button"
          onClick={handleUfsAdd}
          disabled={disabled}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-primary-dark border border-gray-300 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Add UFS row"
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => handleUfsRemove(idx)}
          disabled={disabled || isUfsRowLocked(idx)}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-red-600 border border-gray-300 bg-white hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Remove"
        >
          ×
        </button>
      )}
    </div>
    );
  };

  const ufsContent = (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx}>{ufsInputRow(item, idx)}</div>
      ))}
      {errorUfs && <p className="text-xs text-red-600">{errorUfs}</p>}
    </div>
  );

  const ufsComponent = (
    <div className="space-y-2">
      <label className="text-sm font-medium text-primary-dark">{labelUfs}</label>
      {ufsContent}
    </div>
  );

  if (isTop) {
    return (
      <div className="space-y-3 w-full">
        <div className="space-y-1">
          {labelToEl}
          {toField}
        </div>
        {ufsComponent}
      </div>
    );
  }

  return (
    <div className="space-y-2 w-full">
      <div className="grid items-center gap-y-1" style={gridStyle}>
        {labelToEl}
        {toField}
      </div>
      <div className="grid items-start gap-y-1" style={gridStyle}>
        {labelUfsEl}
        {ufsContent}
      </div>
      {errorTo && (
        <p className="text-xs text-red-600 col-span-2">{errorTo}</p>
      )}
    </div>
  );
};

export default ToAndUfs;
